import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import './style/Dashboard.css';
import Navbar from './utils/Navbr';

// --- NEW: Number Scrolling Animation Component ---
const CountUp = ({ end, duration = 2000, prefix = '', decimals = 0 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        let startTime = null;
        const startValue = 0;
        const endValue = parseFloat(end) || 0;

        const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = timestamp - startTime;
            const percentage = Math.min(progress / duration, 1);
            const ease = (x) => (x === 1 ? 1 : 1 - Math.pow(2, -10 * x));
            const current = startValue + (endValue - startValue) * ease(percentage);
            setCount(current);

            if (progress < duration) {
                window.requestAnimationFrame(step);
            }
        };

        window.requestAnimationFrame(step);
    }, [end, duration]);

    const formatted = count.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });

    return <>{prefix}{formatted}</>;
};

// --- Custom Graph Component (Fixed for Mobile & Desktop Scrolling) ---
const PerformanceGraph = ({ data }) => {
    const [hoveredPoint, setHoveredPoint] = useState(null);
    const graphRef = useRef(null);
    const scrollContainerRef = useRef(null);

    // State for drag-to-scroll (Desktop only)
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const width = 800;
    const height = 300;
    const padding = 40;

    if (!data || data.length === 0) return <div className="no-data-graph-unique">No Data Available</div>;

    const maxClick = Math.max(...data.map(d => d.clicks)) || 1;
    const maxConv = Math.max(...data.map(d => d.conversions)) || 1;
    const maxEarn = Math.max(...data.map(d => d.earnings)) || 1;

    const getY = (val, max) => height - padding - ((val / max) * (height - (padding * 2)));
    const getX = (index) => padding + (index * ((width - (padding * 2)) / (data.length - 1)));

    const generatePath = (key, max) => {
        let d = `M ${getX(0)} ${getY(data[0][key], max)}`;
        for (let i = 0; i < data.length - 1; i++) {
            const x_mid = (getX(i) + getX(i + 1)) / 2;
            const y_curr = getY(data[i][key], max);
            const y_next = getY(data[i + 1][key], max);
            d += ` C ${x_mid} ${y_curr}, ${x_mid} ${y_next}, ${getX(i + 1)} ${y_next}`;
        }
        return d;
    };

    // --- Mouse Drag Handlers (Desktop) ---
    const handleMouseDown = (e) => {
        // Only trigger for left mouse button and ensure it's not a touch event emulating a mouse
        // (Though React synthetic events usually separate them, this is a safety check)
        setIsDragging(true);
        setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
        setScrollLeft(scrollContainerRef.current.scrollLeft);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - scrollContainerRef.current.offsetLeft;
        const walk = (x - startX) * 1.5; // Scroll speed multiplier
        scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    };

    return (
        <div className="graph-card-unique" ref={graphRef}>
            <style>
                {`
                    .graph-scroll-container::-webkit-scrollbar {
                        display: none;
                    }
                    .graph-scroll-container {
                        -ms-overflow-style: none;
                        scrollbar-width: none;
                    }
                `}
            </style>
            <div className="graph-header-fixed">
                <h3 className="graph-title-unique">Weekly Performance</h3>
                <div className="legend-unique">
                    <span className="leg-item-unique click"><span className="dot"></span> Clicks</span>
                    <span className="leg-item-unique conv"><span className="dot"></span> Conv.</span>
                    <span className="leg-item-unique earn"><span className="dot"></span> Earnings</span>
                </div>
            </div>

            <div 
                className="graph-scroll-container"
                ref={scrollContainerRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                style={{ 
                    overflowX: 'auto',       // Enables native scrolling
                    overflowY: 'hidden',
                    whiteSpace: 'nowrap',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    userSelect: 'none',      // Prevents text selection while dragging
                    width: '100%',
                    position: 'relative',
                    touchAction: 'pan-x',    // CRITICAL: Tells mobile browsers to handle horizontal swipes natively
                    WebkitOverflowScrolling: 'touch' // iOS smooth scrolling
                }}
            >
                <svg 
                    viewBox={`0 0 ${width} ${height}`} 
                    className="custom-svg-unique"
                    style={{ 
                        minWidth: '800px', 
                        display: 'block', 
                        pointerEvents: isDragging ? 'none' : 'auto' // Prevents tooltip flickering during drag
                    }} 
                >
                    {[...Array(5)].map((_, i) => (
                        <line key={i} x1={padding} y1={padding + (i * ((height - 2 * padding) / 4))} x2={width - padding} y2={padding + (i * ((height - 2 * padding) / 4))} stroke="rgba(255,255,255,0.05)" />
                    ))}

                    <path d={generatePath('clicks', maxClick)} fill="none" stroke="#3b82f6" strokeWidth="3" className="path-anim" />
                    <path d={generatePath('conversions', maxConv)} fill="none" stroke="#8b5cf6" strokeWidth="3" className="path-anim delay-1" />
                    <path d={generatePath('earnings', maxEarn)} fill="none" stroke="#10b981" strokeWidth="3" className="path-anim delay-2" />

                    {data.map((item, i) => (
                        <g key={i}>
                            <rect
                                x={getX(i) - 20} y="0" width="40" height={height}
                                fill="transparent"
                                onMouseEnter={() => !isDragging && setHoveredPoint({ ...item, x: getX(i) })}
                                onMouseLeave={() => setHoveredPoint(null)}
                                style={{ cursor: 'pointer' }}
                            />
                            <circle cx={getX(i)} cy={getY(item.clicks, maxClick)} r="4" fill="#3b82f6" className="graph-dot" />
                            <circle cx={getX(i)} cy={getY(item.conversions, maxConv)} r="4" fill="#8b5cf6" className="graph-dot" />
                            <circle cx={getX(i)} cy={getY(item.earnings, maxEarn)} r="4" fill="#10b981" className="graph-dot" />
                            <text x={getX(i)} y={height - 10} fill="#a1a1aa" fontSize="12" textAnchor="middle">{item.day}</text>
                        </g>
                    ))}
                </svg>

                {hoveredPoint && !isDragging && (
                    <div
                        className="graph-tooltip-unique"
                        style={{ left: `${(hoveredPoint.x / width) * 100}%` }}
                    >
                        <div className="tooltip-header">{hoveredPoint.day} Stats</div>
                        <div className="tooltip-row click">Clicks: <span>{hoveredPoint.clicks}</span></div>
                        <div className="tooltip-row conv">Conv: <span>{hoveredPoint.conversions}</span></div>
                        <div className="tooltip-row earn">Earn: <span>₹{hoveredPoint.earnings}</span></div>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Main Dashboard Component ---
const Dashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [dashboardData, setDashboardData] = useState({
        graphData: [],
        stats: {
            clicks: { today: 0, yesterday: 0, mtd: 0 },
            conversions: { today: 0, yesterday: 0, mtd: 0 },
            earnings: { today: 0, yesterday: 0, mtd: 0 }
        },
        trendingOffers: [],
        userTopOffer: null
    });

    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    const fetchStats = async () => {
        const authToken = getCookie('authToken');
        try {
            const response = await axios.post(
                'https://publisher.cashbolt.in.net/server/',
                {
                    action: 'fetchDashboardStats',
                    authToken
                }
            );

            if (response.data.status === false) {
                toast.error(response.data.message || 'Failed to load dashboard data');
                return;
            }

            setDashboardData({
                graphData: response.data.graphData || [],
                stats: response.data.stats || {
                    clicks: { today: 0, yesterday: 0, mtd: 0 },
                    conversions: { today: 0, yesterday: 0, mtd: 0 },
                    earnings: { today: 0, yesterday: 0, mtd: 0 }
                },
                trendingOffers: response.data.trendingOffers || [],
                userTopOffer: response.data.userTopOffer || null
            });

        } catch (error) {
            console.error(error);
            toast.error('Failed to load dashboard data');
        }
    };

    useEffect(() => {
        const authToken = getCookie('authToken');
        const fetchData = async () => {
            try {
                setLoading(true);
                const userResponse = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'checkAuth',
                    authToken
                });

                if (userResponse.data.status === false) {
                    toast.error(userResponse.data.message || 'Session expired', 3000);
                    navigate('/login');
                    return;
                } else {
                    setUser(userResponse.data.res);
                    await fetchStats();
                }
            } catch (error) {
                console.error(error);
                toast.error('Failed to load dashboard data');
                navigate('/login');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [navigate]);

    if (loading) return <Loader />;

    return (
        <div className="dashboard-wrapper-unique">
            <Navbar name={user?.name || 'Partner'} role={user?.role || 'Publisher'} />
            <div className="bg-shape-dash shape-d1"></div>
            <div className="bg-shape-dash shape-d2"></div>

            <div className="dashboard-content-unique">
                <div className="dash-header-unique">
                    <h2>Welcome, <span className="highlight-text">{user?.name || 'Partner'}</span></h2>
                    <p className="dash-date">{new Date().toDateString()}</p>
                </div>

                <PerformanceGraph data={dashboardData.graphData} />

                {/* --- Animated Stats --- */}
                <div className="stats-grid-unique">
                    {/* Clicks Container */}
                    <div className="stat-card-unique">
                        <div className="stat-icon-box blue">🖱️</div>
                        <div className="stat-info">
                            <h3>Clicks Overview</h3>
                            <div className="stat-row">
                                <div className="stat-item">
                                    <span>Today</span>
                                    <strong>
                                        <CountUp end={dashboardData.stats.clicks.today} />
                                    </strong>
                                </div>
                                <div className="stat-separator"></div>
                                <div className="stat-item">
                                    <span>Yesterday</span>
                                    <strong>
                                        <CountUp end={dashboardData.stats.clicks.yesterday} />
                                    </strong>
                                </div>
                                <div className="stat-separator"></div>
                                <div className="stat-item">
                                    <span>MTD</span>
                                    <strong>
                                        <CountUp end={dashboardData.stats.clicks.mtd} />
                                    </strong>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Revenue & Conv Container */}
                    <div className="stat-card-unique">
                        <div className="stat-icon-box green">💰</div>
                        <div className="stat-info">
                            <h3>Revenue & Conv.</h3>
                            <div className="stat-row">
                                <div className="stat-item">
                                    <span>Today</span>
                                    <strong className="earn-text">
                                        <CountUp end={dashboardData.stats.earnings.today} prefix="₹" decimals={2} />
                                    </strong>
                                    <small className="conv-text">
                                        <CountUp end={dashboardData.stats.conversions.today} /> cv
                                    </small>
                                </div>
                                <div className="stat-separator"></div>
                                <div className="stat-item">
                                    <span>Yesterday</span>
                                    <strong className="earn-text">
                                        <CountUp end={dashboardData.stats.earnings.yesterday} prefix="₹" decimals={2} />
                                    </strong>
                                    <small className="conv-text">
                                        <CountUp end={dashboardData.stats.conversions.yesterday} /> cv
                                    </small>
                                </div>
                                <div className="stat-separator"></div>
                                <div className="stat-item">
                                    <span>MTD</span>
                                    <strong className="earn-text">
                                        <CountUp end={dashboardData.stats.earnings.mtd} prefix="₹" decimals={2} />
                                    </strong>
                                    <small className="conv-text">
                                        <CountUp end={dashboardData.stats.conversions.mtd} /> cv
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="dash-section-unique full-width">
                    <h3 className="section-title-unique">🔥 Global Trending Offers</h3>
                    <div className="trending-list-unique">
                        {dashboardData.trendingOffers.map((offer) => (
                            <div key={offer.id} className="offer-row-unique">
                                <div className="offer-left">
                                    <img src={offer.logo} alt="" className="offer-logo" />
                                    <div className="offer-meta">
                                        <h4>{offer.name}</h4>
                                        <span>ID: {offer.id}</span>
                                    </div>
                                </div>
                                <div className="offer-stats">
                                    <div className="os-item">
                                        <span>Conv</span>
                                        <strong>{offer.conv}</strong>
                                    </div>
                                    <div className="os-item">
                                        <span>Clicks</span>
                                        <strong>{offer.clicks}</strong>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {dashboardData.userTopOffer && (
                    <div className="dash-section-unique full-width highlight-border">
                        <div className="best-offer-header">
                            <h3>🏆 Your Top Performer</h3>
                            <span className="badge-best">#1 Rank</span>
                        </div>
                        <div className="offer-row-unique no-border">
                            <div className="offer-left">
                                <img src={dashboardData.userTopOffer.logo} alt="" className="offer-logo large" />
                                <div className="offer-meta">
                                    <h4>{dashboardData.userTopOffer.name}</h4>
                                    <span className="highlight-text">Total Revenue Leader</span>
                                </div>
                            </div>
                            <div className="offer-stats big-stats">
                                <div className="os-item">
                                    <span>Total Conv</span>
                                    <strong>{dashboardData.userTopOffer.conv}</strong>
                                </div>
                                <div className="os-item">
                                    <span>Total Clicks</span>
                                    <strong>{dashboardData.userTopOffer.clicks}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard;